/**
 * Azioni sul CRM proposte dall'assistente, dentro la card del suggerimento:
 * ogni azione ha "Conferma" e "Scarta", niente parte da solo.
 * Riusa i percorsi del pannello (useChatLeadControls -> clientStore.updateClient)
 * e, per la fase persa, lo stesso drawer col motivo di ChatLeadControls.
 * Si mostra solo con un Lead/Cliente collegato alla chat.
 */
import { useState } from 'react';
import { Check, Flag, FileText, Loader2, PencilLine, UserPlus, X, XCircle } from 'lucide-react';
import type { Client } from '@/lib/types';
import type { AgentRequest } from '@/lib/types/whatsappAgent';
import type { MarkLostAction } from '../../../../supabase/functions/whatsapp-agent-analyze/crmActions';
import { useAuth } from '@/lib/auth';
import { useApp } from '@/components/AppProvider';
import { usePermissions } from '@/lib/hooks/usePermissions';
import LeadStatusUpdateDrawer from '@/components/leads/LeadStatusUpdateDrawer';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { newDealUrl } from '@/lib/utils/dealWhatsApp';
import { useChatLeadControls } from './useChatLeadControls';
import {
  pendingCrmActions, crmActionTitle, crmActionConfirmLabel, crmActionBlock,
  pendingLeadChanges, leadUpdatesFromAction, type CrmAction,
} from './suggestionCrmActionsLogic';
import { saveCrmActionStatus } from './suggestionCrmActionsService';

const ICONS: Record<CrmAction['kind'], typeof Flag> = {
  move_stage: Flag, mark_lost: XCircle, create_deal: FileText, update_lead: PencilLine, assign: UserPlus,
};

export function SuggestionCrmActions({ suggestion, client }: { suggestion: AgentRequest; client: Client }) {
  const { user } = useAuth();
  const { toast } = useApp();
  const { canCreateDeal } = usePermissions();
  const c = useChatLeadControls(client);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [lostAction, setLostAction] = useState<MarkLostAction | null>(null);

  const actions = pendingCrmActions(suggestion.crm_actions);
  if (actions.length === 0) return null;

  const lead = client as unknown as Record<string, unknown>;
  const ctx = { stageIds: c.stages.map((s) => s.id), currentStageId: c.currentStageId, userIds: c.userOptions.map((u) => u.id) };

  const record = async (actionId: string, status: 'applied' | 'dismissed') => {
    const ok = await saveCrmActionStatus(suggestion, actionId, status, user?.id ?? null);
    if (!ok) toast.error(status === 'applied'
      ? 'Fatto sulla scheda, ma non sono riuscito a segnarlo nel suggerimento'
      : 'Non sono riuscito a scartare la proposta, riprova');
  };

  const onConfirm = async (action: CrmAction) => {
    if (action.kind === 'mark_lost') { setLostAction(action); return; }
    if (action.kind === 'create_deal') {
      window.open(newDealUrl(client.id), '_blank', 'noopener');
      if (action.services.length > 0) toast.info(`Aggiungi all'offerta: ${action.services.map((s) => s.title).join(', ')}`);
      await record(action.id, 'applied');
      return;
    }
    setBusyId(action.id);
    try {
      const ok = action.kind === 'move_stage' ? await c.setStage(action.stage_id)
        : action.kind === 'assign' ? await c.setAssignee(action.user_id)
          : await c.updateFields(leadUpdatesFromAction(action, lead));
      if (ok) await record(action.id, 'applied');
    } finally {
      setBusyId(null);
    }
  };

  const onDismiss = async (action: CrmAction) => {
    setBusyId(action.id);
    try { await record(action.id, 'dismissed'); } finally { setBusyId(null); }
  };

  return (
    <div className="ml-6 mt-1.5 space-y-1">
      {actions.map((action) => {
        const Icon = ICONS[action.kind];
        const block = crmActionBlock(action, lead, ctx);
        const noPermission = action.kind === 'create_deal' ? !canCreateDeal : !c.canEdit;
        const disabled = !!block || noPermission || busyId === action.id;
        const why = block || (noPermission ? 'Non hai il permesso per questa modifica' : null);
        return (
          <div key={action.id} className="rounded-sm border border-[rgba(35,131,226,0.18)] bg-[rgba(35,131,226,0.03)] dark:bg-[#2383E2]/5 px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              <Icon className="w-3 h-3 text-[#2383E2] flex-shrink-0" />
              <span className="text-[11px] font-medium text-[#37352F] dark:text-white/90 flex-1 min-w-0 truncate">{crmActionTitle(action)}</span>
            </div>
            {action.reason && <p className="text-[10px] text-[#6F6E69] mt-0.5 leading-snug line-clamp-2">{action.reason}</p>}
            {action.kind === 'update_lead' && (
              <ul className="mt-1 space-y-0.5">
                {pendingLeadChanges(action, lead).map((ch) => (
                  <li key={ch.field} className="text-[10px] text-[#37352F] dark:text-white/80">
                    <span className="text-[#6F6E69]">{ch.label}: </span>
                    <span className="line-through text-[#6F6E69]">{ch.before || 'vuoto'}</span>
                    <span className="text-[#6F6E69]"> diventa </span>
                    <span className="font-medium">{ch.after}</span>
                  </li>
                ))}
              </ul>
            )}
            {action.kind === 'create_deal' && action.services.length > 0 && (
              <p className="text-[10px] text-[#37352F] dark:text-white/80 mt-0.5">Servizi: {action.services.map((s) => s.title).join(', ')}</p>
            )}
            {why && <p className="text-[10px] text-[#F2994A] mt-0.5">{why}</p>}
            <div className="flex items-center gap-1 mt-1">
              <button onClick={() => { void onConfirm(action); }} disabled={disabled}
                {...tip(action.kind === 'create_deal' ? "Apre la creazione dell'offerta in una nuova scheda con il Lead/Cliente già scelto" : 'Applica la modifica alla scheda del Lead/Cliente')}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-white bg-[#2383E2] hover:bg-[#1B6EC2] disabled:opacity-50 rounded-sm transition-colors">
                {busyId === action.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" />} {crmActionConfirmLabel(action)}
              </button>
              <button onClick={() => { void onDismiss(action); }} disabled={busyId === action.id}
                {...iconTip("Scarta questa proposta dell'assistente")}
                className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.04)] rounded-sm transition-colors">
                <X className="w-2.5 h-2.5" /> Scarta
              </button>
            </div>
          </div>
        );
      })}

      {lostAction && (
        <LeadStatusUpdateDrawer
          isOpen
          onClose={() => setLostAction(null)}
          currentStage={lostAction.stage_id}
          leadId={client.id}
          currentAssignee={client.assigned_to || undefined}
          currentSalesRepId={client.sales_rep_id || undefined}
          leadStages={c.stages}
          leadPipelineId={c.pipelineId}
          onStageUpdated={() => setLostAction(null)}
          onNotesUpdated={() => {
            // Come in ChatLeadControls: il drawer scrive nota e motivo, la fase la scrive chi lo apre.
            const action = lostAction;
            setLostAction(null);
            void (async () => { if (await c.setStage(action.stage_id)) await record(action.id, 'applied'); })();
          }}
          translations={{}}
          defaultTab="note"
          requireNote
          defaultStatusOpen
          priorStageId={c.currentStageId || undefined}
          initialLostReason={lostAction.lost_reason}
          initialNoteText={lostAction.reason}
        />
      )}
    </div>
  );
}
